import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AttachmentIndicators, KPICard, PageRibbon, ScoreBadge, TimeWindow } from '../../components';
import {
  DateTimeRangeFilter,
  MultiSelectFilter,
  NumberRangeFilter,
} from '../../components/ColumnFilter';
import { UserFilter } from '../../components/UserFilter';
import { GeneratedTextIcon } from '../../components/icons/GeneratedTextIcon';
import { useAuth } from '../../context';
import { apiService } from '../../services';
import type { ListPromptsRequest, MeAnalytics, PaginationInfo, Prompt, User } from '../../types';
import {
  DOMAIN_FILTER_OPTIONS,
  INTENT_FILTER_OPTIONS,
  formatDomainLabel,
  formatIntentLabel,
  isPenaltyIntent,
} from '../../utils/classificationLabels';
import { defaultRangeDays, kpiAbsoluteDelta, type RangePresetDays } from '../../utils/formatKpi';
import { formatTimestamp } from '../../utils/formatTimestamp';
import { pageItems, pageRange } from '../../utils/pagination';
import { promptAttachmentFlags } from '../../utils/promptListAttachments';
import { userDisplayName } from '../../utils/userDisplayName';
import './Dashboard.css';

function localDateTimeToIso(date: string, time: string, fallbackTime: string): string | undefined {
  if (!date) return undefined;
  const parsed = new Date(`${date}T${time || fallbackTime}`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function pickLaterIso(windowIso: string, filterIso?: string): string {
  if (!filterIso) return windowIso;
  return new Date(filterIso) > new Date(windowIso) ? filterIso : windowIso;
}

function pickEarlierIso(windowIso: string, filterIso?: string): string {
  if (!filterIso) return windowIso;
  return new Date(filterIso) < new Date(windowIso) ? filterIso : windowIso;
}

function parseScoreBound(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.min(10, Math.max(0, parsed));
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isLeadership = user?.role === 'leadership';
  const [days, setDays] = useState<RangePresetDays>(7);
  const range = defaultRangeDays(days);
  const [coaching, setCoaching] = useState<MeAnalytics | null>(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [promptsError, setPromptsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [intents, setIntents] = useState<string[]>([]);
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState('');
  const [toTime, setToTime] = useState('23:59');
  const [sortBy, setSortBy] = useState<'score' | 'timestamp'>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchKPIs = useCallback(async () => {
    setKpisLoading(true);
    setKpisError(null);
    try {
      const emails = isLeadership ? selectedEmails : undefined;
      const response = await apiService.getMeAnalytics(range.start, range.end, emails);
      if (response.success) setCoaching(response.data);
      else setKpisError(response.message);
    } catch (error) {
      setKpisError(error instanceof Error ? error.message : 'Failed to load coaching metrics');
    } finally {
      setKpisLoading(false);
    }
  }, [range.start, range.end, isLeadership, selectedEmails]);

  const fetchPrompts = useCallback(async (nextPage: number) => {
    setPromptsLoading(true);
    setPromptsError(null);
    try {
      const emails = isLeadership
        ? (selectedEmails.length ? selectedEmails : undefined)
        : (user?.email ? [user.email] : undefined);
      const windowFrom = `${range.start}T00:00:00.000Z`;
      const windowTo = `${range.end}T23:59:59.999Z`;
      const parsedMin = parseScoreBound(minScore);
      const parsedMax = parseScoreBound(maxScore);
      const params: ListPromptsRequest = {
        page: nextPage,
        per_page: 20,
        emails,
        from_date: pickLaterIso(windowFrom, localDateTimeToIso(fromDate, fromTime, '00:00')),
        to_date: pickEarlierIso(windowTo, localDateTimeToIso(toDate, toTime, '23:59')),
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (domains.length) params.domains = domains;
      if (intents.length) params.intents = intents;
      if (search) params.search = search;
      if (parsedMin !== undefined) params.min_score = parsedMin;
      if (parsedMax !== undefined) params.max_score = parsedMax;
      const response = await apiService.listPrompts(params);
      if (response.success) {
        setPrompts(response.data.prompts);
        setPagination(response.data.pagination);
      } else {
        setPromptsError(response.message);
      }
    } catch (error) {
      setPromptsError(error instanceof Error ? error.message : 'Failed to load prompts');
    } finally {
      setPromptsLoading(false);
    }
  }, [
    range.start,
    range.end,
    user?.email,
    isLeadership,
    selectedEmails,
    domains,
    intents,
    minScore,
    maxScore,
    fromDate,
    fromTime,
    toDate,
    toTime,
    search,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (!isLeadership) return;
    setUsersLoading(true);
    apiService.getUsers({ full_list: true })
      .then((response) => {
        if (response.success) setUsers(response.data.users);
      })
      .finally(() => setUsersLoading(false));
  }, [isLeadership]);

  const displayUsers = useMemo((): User[] => {
    if (isLeadership && users.length > 0) return users;
    if (!user?.email) return [];
    return [{
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      avg_score: null,
      prompts_count: 0,
    }];
  }, [isLeadership, users, user]);

  useEffect(() => {
    if (!user?.email) return;
    fetchKPIs();
  }, [user?.email, fetchKPIs]);

  useEffect(() => {
    if (!user?.email) return;
    setPage(1);
    fetchPrompts(1);
  }, [user?.email, fetchPrompts]);

  const goToPage = (next: number) => {
    setPage(next);
    fetchPrompts(next);
  };

  const perPage = pagination?.per_page || 20;
  const totalPages = pagination
    ? pagination.total_pages || Math.ceil(pagination.total_count / perPage)
    : 0;
  const { start: pageRangeStart, end: pageRangeEnd } = pagination
    ? pageRange(page, perPage, pagination.total_count)
    : { start: 0, end: 0 };

  const scored = coaching?.scored_count ?? coaching?.prompt_count ?? 0;
  const pending = coaching?.pending_count ?? 0;
  const avgTrend = kpiAbsoluteDelta(coaching?.avg_score, coaching?.prior?.avg_score, days);
  const penaltyTrend = kpiAbsoluteDelta(
    coaching?.penalty_count ?? 0,
    coaching?.prior?.penalty_count ?? 0,
    days,
    0
  );
  const penaltyTrendWarn = penaltyTrend
    ? {
        ...penaltyTrend,
        direction: (penaltyTrend.direction === 'up' ? 'down' : 'up') as 'up' | 'down',
      }
    : null;

  const subtitle = isLeadership
    ? (selectedEmails.length
      ? `Prompts for the selected people from the last ${days} days. Open one to see how its score was made.`
      : `All prompts from the last ${days} days. Open one to see how its score was made.`)
    : `Your prompts from the last ${days} days. Open one to see how its score was made.`;

  return (
    <div className="dashboard">
      <main className="dashboard__content">
        <PageRibbon
          title="Prompts"
          icon={<GeneratedTextIcon size={20} />}
          subtitle={subtitle}
          actions={<TimeWindow value={days} onChange={setDays} />}
        />
        {kpisError && <p className="dashboard__error">{kpisError}</p>}
        <div className="dashboard__kpis">
          <KPICard
            title="Prompts scored"
            value={scored}
            format="length"
            loading={kpisLoading}
            subtitle={pending > 0 ? `${pending} more waiting to be scored` : 'All prompts in this window are scored'}
            variant="primary"
          />
          <KPICard
            title="Avg overall score"
            value={coaching?.avg_score != null ? coaching.avg_score.toFixed(1) : null}
            format="text"
            loading={kpisLoading}
            subtitle="/ 10"
            trend={avgTrend}
            variant="success"
          />
          <KPICard
            title="Prompts with a penalty"
            value={coaching?.penalty_count ?? 0}
            format="length"
            loading={kpisLoading}
            trend={penaltyTrendWarn}
            variant="info"
          />
        </div>

        {promptsError && <p className="dashboard__error">{promptsError}</p>}
        <div className="pa-card">
          <div className="pa-toolbar">
            <label className="pa-search">
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search prompt text"
                aria-label="Search prompt text"
              />
            </label>
            <div className="pa-toolbar__aside">
              {isLeadership && (
                <UserFilter
                  users={users}
                  selectedEmails={selectedEmails}
                  onChange={(emails) => {
                    setSelectedEmails(emails);
                    setPage(1);
                  }}
                  usersLoading={usersLoading}
                />
              )}
              <div className="pa-pager__legend-box" aria-label="Attachment legend">
                <span className="pa-pager__legend-item">
                  <AttachmentIndicators hasSkill hasRule={false} hasAgent={false} />
                  Skill
                </span>
                <span className="pa-pager__legend-item">
                  <AttachmentIndicators hasSkill={false} hasRule hasAgent={false} />
                  Rule
                </span>
                <span className="pa-pager__legend-item">
                  <AttachmentIndicators hasSkill={false} hasRule={false} hasAgent />
                  Agent
                </span>
              </div>
            </div>
          </div>
          <div className="pa-table-wrap">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>
                    <div className="pa-th">
                      <span>Timestamp</span>
                      <DateTimeRangeFilter
                        fromDate={fromDate}
                        fromTime={fromTime}
                        toDate={toDate}
                        toTime={toTime}
                        ariaLabel="Filter by timestamp"
                        onApply={(next) => {
                          setFromDate(next.fromDate);
                          setFromTime(next.fromTime || '00:00');
                          setToDate(next.toDate);
                          setToTime(next.toTime || '23:59');
                          setPage(1);
                        }}
                      />
                    </div>
                  </th>
                  <th>User</th>
                  <th>Prompt</th>
                  <th>
                    <div className="pa-th">
                      <span>Domain</span>
                      <MultiSelectFilter
                        options={DOMAIN_FILTER_OPTIONS}
                        value={domains}
                        ariaLabel="Filter by domain"
                        onApply={(next) => {
                          setDomains(next);
                          setPage(1);
                        }}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="pa-th">
                      <span>Intent</span>
                      <MultiSelectFilter
                        options={INTENT_FILTER_OPTIONS}
                        value={intents}
                        ariaLabel="Filter by intent"
                        onApply={(next) => {
                          setIntents(next);
                          setPage(1);
                        }}
                      />
                    </div>
                  </th>
                  <th>Attached</th>
                  <th className="pa-table__num">
                    <div className="pa-th pa-th--end">
                      <button
                        type="button"
                        className={`pa-table__sort-btn${sortBy === 'score' ? ' pa-table__sort-btn--active' : ''}`}
                        onClick={() => {
                          if (sortBy !== 'score') {
                            setSortBy('score');
                            setSortOrder('desc');
                          } else {
                            setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'));
                          }
                          setPage(1);
                        }}
                        aria-label={
                          sortBy === 'score'
                            ? `Sort overall score ${sortOrder === 'asc' ? 'descending' : 'ascending'}`
                            : 'Sort by overall score'
                        }
                      >
                        Overall
                        <span className="pa-table__sort-arrows" aria-hidden>
                          <span className={sortBy === 'score' && sortOrder === 'asc' ? 'is-on' : ''}>▲</span>
                          <span className={sortBy === 'score' && sortOrder === 'desc' ? 'is-on' : ''}>▼</span>
                        </span>
                      </button>
                      <NumberRangeFilter
                        min={minScore}
                        max={maxScore}
                        ariaLabel="Filter by overall score"
                        onApply={(next) => {
                          setMinScore(next.min);
                          setMaxScore(next.max);
                          setPage(1);
                        }}
                      />
                    </div>
                  </th>
                  <th className="pa-table__num"> </th>
                </tr>
              </thead>
              <tbody>
                {promptsLoading && prompts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="dashboard__muted">Loading prompts…</td>
                  </tr>
                ) : prompts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="dashboard__muted">
                      No prompts match the current filters.
                    </td>
                  </tr>
                ) : (
                  prompts.map((prompt) => {
                    const attachments = promptAttachmentFlags(prompt.score_details);
                    return (
                    <tr key={prompt.id}>
                      <td className="dashboard__muted dashboard__timestamp-cell">{formatTimestamp(prompt.timestamp)}</td>
                      <td className="dashboard__user-cell">
                        {userDisplayName(prompt.email, displayUsers)}
                      </td>
                      <td className="pa-table__prompt">
                        <Link className="pa-table__prompt-link" to={`/prompts/${prompt.id}`}>
                          {prompt.prompt_text}
                        </Link>
                      </td>
                      <td>
                        <span className="pill pill--domain">{formatDomainLabel(prompt.task_domain)}</span>
                      </td>
                      <td>
                        <span className={`pill${isPenaltyIntent(prompt.task_category) ? ' pill--penalty' : ''}`}>
                          {formatIntentLabel(prompt.task_category)}
                        </span>
                      </td>
                      <td>
                        <AttachmentIndicators {...attachments} />
                      </td>
                      <td className="pa-table__num"><ScoreBadge score={prompt.overall_score} /></td>
                      <td className="pa-table__num">
                        <Link className="pa-row-link" to={`/prompts/${prompt.id}`}>View breakdown</Link>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {pagination && (
            <div className="pa-pager">
              <span className="pa-pager__summary">
                Showing {pageRangeStart}–{pageRangeEnd} of {pagination.total_count} prompts · {
                  sortBy === 'score'
                    ? (sortOrder === 'desc' ? 'highest first' : 'lowest first')
                    : 'newest first'
                }
              </span>
              <div className="pa-pager__end">
                <div className="pa-pager__btns">
                <button
                  type="button"
                  className="pa-btn"
                  disabled={page <= 1 || promptsLoading}
                  onClick={() => goToPage(page - 1)}
                >
                  Previous
                </button>
                {pageItems(page, totalPages).map((item, index) =>
                  item === 'ellipsis' ? (
                    <span key={`ellipsis-${index}`} className="pa-pager__ellipsis" aria-hidden="true">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`pa-btn pa-pager__page${item === page ? ' pa-pager__page--active' : ''}`}
                      aria-label={`Page ${item}`}
                      aria-current={item === page ? 'page' : undefined}
                      disabled={item === page || promptsLoading}
                      onClick={() => goToPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="pa-btn"
                  disabled={page >= totalPages || promptsLoading}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <p className="dashboard__footnote">
          {isLeadership
            ? 'Leadership can open any prompt. Score is out of 10 and is explained on each prompt.'
            : 'Only your own prompts appear here. Score is out of 10 and is explained on each prompt.'}
        </p>
      </main>
    </div>
  );
};

export default Dashboard;
